import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import type { User as FirebaseUser } from "firebase/auth";
import { auth, db } from "../../config/firebase";

interface UserData {
  uid: string;
  email: string;
  firstName: string;
  lastName: string;
  organizations: {
    [orgId: string]: {
      role: "owner" | "member";
      joinedAt: any; // Timestamp
    };
  };
  role?: string; // Add special admin role field
  createdAt: any; // Timestamp
  updatedAt: any; // Timestamp-
}

interface UserState {
  user: UserData | null;
  userFirebase: UserData | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: UserState = {
  user: null,
  userFirebase: null,
  status: "idle",
  error: null,
};

// Async thunk for fetching user data from Firestore
export const fetchUserData = createAsyncThunk(
  "user/fetchUserData",
  async (uid: string, { rejectWithValue }) => {
    try {
      const userDocRef = doc(db, "users", uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        setUser(userDoc.data());
        return userDoc.data() as UserData;
      } else {
        return rejectWithValue("User data not found");
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.userFirebase = action.payload;
    },
    clearUser: (state) => {
      state.user = null;
      state.userFirebase = null;
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserData.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.user = action.payload;
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
  },
});

export const { setUser, clearUser } = userSlice.actions;
export default userSlice.reducer;

// Initialize auth listener
export const initAuthListener = (dispatch) => {
  return onAuthStateChanged(auth, async (user) => {
    dispatch(setUser(user));

    if (user) {
      dispatch(fetchUserData(user.uid));
    }
  });
};
